<template>
    <div class="user-toplist-table">
        <div class="user-toplist-table__controls">
            <Select
                v-model="selectedYear"
                :options="years"
                style="width: 8rem"
            />

            <button
                type="button"
                class="user-toplist-table__unit-btn"
                :class="{ 'user-toplist-table__unit-btn--active': unit === 'kg' }"
                @click="unit = 'kg'"
            >
                Kg
            </button>
            <button
                type="button"
                class="user-toplist-table__unit-btn"
                :class="{ 'user-toplist-table__unit-btn--active': unit === 'liters' }"
                @click="unit = 'liters'"
            >
                Liter
            </button>
        </div>

        <DataTable
            v-if="!initialized || hasData"
            :value="rankedTableRows"
            :loading="loading"
            dataKey="id"
            stripedRows
            sortMode="single"
            v-model:sortField="sortField"
            v-model:sortOrder="sortOrder"
            :defaultSortOrder="-1"
        >
            <Column field="rank" header="#" style="width: 3rem; max-width: 3rem" />

            <Column field="isAnonymous" header="" style="width: 2.5rem; max-width: 2.5rem">
                <template #body="{ data }">
                    <i
                        v-if="data.isAnonymous"
                        class="pi pi-eye-slash user-toplist-table__anonymous-icon"
                        aria-label="Anonym"
                    ></i>
                </template>
            </Column>

            <Column field="name" header="Bruker">
                <template #body="{ data }">
                    <span
                        class="user-toplist-table__link"
                        @click="openRegistrations(data, null)"
                    >{{ data.name }}</span>
                </template>
            </Column>


            <Column
                v-for="item of items"
                :key="item.id"
                :field="`item_${item.id}`"
                sortable
            >
                <template #header>
                    <img
                        v-if="item.illustration_src"
                        :src="item.illustration_src"
                        :alt="item.illustration_alt || item.name"
                        class="user-toplist-table__header-icon"
                    >
                    {{ item.name }}
                </template>
                <template #body="{ data }">
                    <span
                        v-if="data[`item_${item.id}`] > 0"
                        class="user-toplist-table__link"
                        @click="openRegistrations(data, item)"
                    >{{ formatAmount(data[`item_${item.id}`]) }}</span>
                    <template v-else>{{ formatAmount(data[`item_${item.id}`]) }}</template>
                </template>
            </Column>

            <Column field="total" :header="`Totalt (${unitLabel})`" sortable>
                <template #body="{ data }">
                    {{ formatAmount(data.total) }}
                </template>
            </Column>
        </DataTable>

        <p v-else class="user-toplist-table__empty">
            Ingen registreringer for {{ selectedYear }}.
        </p>

        <UserRegistrationsDialog
            v-model:visible="registrationsDialogVisible"
            :user="registrationsDialogUser"
            :itemId="registrationsDialogItem ? registrationsDialogItem.id : null"
            :itemName="registrationsDialogItem ? registrationsDialogItem.name : null"
            :year="selectedYear"
        ></UserRegistrationsDialog>
    </div>
</template>
<script>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Select from 'primevue/select';
import UserRegistrationsDialog from './UserRegistrationsDialog';

export default {
    components: {
        DataTable,
        Column,
        Select,
        UserRegistrationsDialog,
    },
    props: {
        groupId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            items: [],
            users: [],
            years: [],
            selectedYear: new Date().getFullYear(),
            unit: 'kg',
            loading: true,
            registrationsDialogVisible: false,
            registrationsDialogUser: null,
            registrationsDialogItem: null,
            initialized: false,
            sortField: 'total',
            sortOrder: -1,
        };
    },
    computed: {
        unitLabel() {
            return this.unit === 'kg' ? 'kg' : 'liter';
        },
        hasData() {
            return this.tableRows.length > 0;
        },
        tableRows() {
            return this.users.map(user => {
                const row = {
                    id: user.id,
                    name: user.name,
                    isAnonymous: user.is_anonymous,
                    total: user.total[this.unit],
                };

                this.items.forEach(item => {
                    row[`item_${item.id}`] = user.items[item.id] ? user.items[item.id][this.unit] : 0;
                });

                return row;
            });
        },
        rankedTableRows() {
            const field = this.sortField || 'total';
            const order = this.sortOrder || -1;

            // Rank ties on the value shown in the table (formatAmount, one
            // decimal), otherwise rows that display the same number can
            // still get different ranks.
            const displayValue = row => {
                const value = row[field];
                return typeof value === 'number' ? Math.round(value * 10) / 10 : value;
            };

            const sorted = [...this.tableRows].sort((a, b) => {
                const aValue = displayValue(a);
                const bValue = displayValue(b);

                if (aValue === bValue) {
                    return 0;
                }

                return aValue > bValue ? order : -order;
            });

            let rank = 0;
            let lastValue = null;

            return sorted.map(row => {
                const value = displayValue(row);

                if (lastValue === null || value !== lastValue) {
                    rank++;
                    lastValue = value;
                }

                return { ...row, rank };
            });
        },
    },
    watch: {
        groupId() {
            this.fetchData();
        },
        selectedYear() {
            this.fetchData();
        },
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.loading = true;
            const startedAt = Date.now();

            try {
                const response = await fetch(`/api/v1/toplist/itemgroup/${this.groupId}/users?year=${this.selectedYear}`);
                const data = await response.json();
                this.items = data.items;
                this.users = data.users;

                const currentYear = new Date().getFullYear();
                this.years = [...new Set([...data.years, currentYear])].sort((a, b) => b - a);
            } catch (error) {
                console.error(error);
            } finally {
                // Local/fast responses can resolve in a few milliseconds, which makes
                // the loading spinner flash imperceptibly. Keep it visible briefly so
                // it's actually noticeable regardless of connection speed.
                const minVisibleMs = 300;
                const elapsed = Date.now() - startedAt;

                if (elapsed < minVisibleMs) {
                    await new Promise(resolve => setTimeout(resolve, minVisibleMs - elapsed));
                }

                this.loading = false;
                this.initialized = true;
            }
        },
        formatAmount(amount) {
            return (amount || 0).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        },
        openRegistrations(row, item) {
            this.registrationsDialogUser = { id: row.id, name: row.name };
            this.registrationsDialogItem = item;
            this.registrationsDialogVisible = true;
        },
    },
};
</script>
<style scoped>
.user-toplist-table__controls {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
}

.user-toplist-table__unit-btn {
    background: none;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 0.1rem 0.8rem;
    cursor: pointer;
}

.user-toplist-table__unit-btn--active {
    background: #e9ecef;
    font-weight: bold;
}

.user-toplist-table__empty {
    color: #6c757d;
}

.user-toplist-table__link {
    cursor: pointer;
}

.user-toplist-table__link:hover {
    color: #0d6efd;
}

.user-toplist-table__header-icon {
    width: 20px;
    height: 20px;
    object-fit: contain;
    vertical-align: middle;
    margin-right: 6px;
}

.user-toplist-table__anonymous-icon {
    color: #6c757d;
}
</style>
